import { useIsomorphicLayoutEffect } from 'react-use';
import { getIsMobile } from './getIsMobile';
import { getWindow } from './getWindow';

let lockCount = 0;
let restoreScrollLock: VoidFunction | undefined;

const lockBodyScroll = () => {
    const window = getWindow();

    if (!window) {
        return false;
    }

    lockCount += 1;

    if (lockCount > 1) {
        return true;
    }

    const { body, documentElement } = window.document;
    const { isIos } = getIsMobile();
    const previousBodyStyles = {
        left: body.style.left,
        overflow: body.style.overflow,
        position: body.style.position,
        top: body.style.top,
        width: body.style.width,
    };
    const previousHtmlMinHeight = documentElement.style.getPropertyValue('min-height');
    const previousHtmlMinHeightPriority = documentElement.style.getPropertyPriority('min-height');
    const previousHtmlOverflow = documentElement.style.overflow;
    const scrollX = Math.min(
        Math.max(window.scrollX, 0),
        Math.max(documentElement.scrollWidth - documentElement.clientWidth, 0),
    );
    const scrollY = Math.min(
        Math.max(window.scrollY, 0),
        Math.max(documentElement.scrollHeight - documentElement.clientHeight, 0),
    );
    const href = window.location.href;

    body.style.overflow = 'hidden';

    if (isIos) {
        const maximumViewportSize = Math.max(
            documentElement.clientHeight,
            window.screen.width,
            window.screen.height,
        );
        const lockedDocumentMinHeight = Math.ceil(
            Math.max(documentElement.scrollHeight, scrollY + maximumViewportSize + 1),
        );

        documentElement.style.setProperty(
            'min-height',
            `${String(lockedDocumentMinHeight)}px`,
            'important',
        );
        documentElement.style.overflow = 'hidden';
        Object.assign(body.style, {
            left: `${String(-scrollX)}px`,
            position: 'fixed',
            top: `${String(-scrollY)}px`,
            width: '100%',
        });
    }

    restoreScrollLock = () => {
        Object.assign(body.style, previousBodyStyles);

        if (isIos) {
            if (previousHtmlMinHeight) {
                documentElement.style.setProperty(
                    'min-height',
                    previousHtmlMinHeight,
                    previousHtmlMinHeightPriority,
                );
            } else {
                documentElement.style.removeProperty('min-height');
            }

            documentElement.style.overflow = previousHtmlOverflow;

            if (window.location.href === href) {
                window.scrollTo({ behavior: 'auto', left: scrollX, top: scrollY });
            }
        }
    };

    return true;
};

const unlockBodyScroll = () => {
    if (lockCount === 0) {
        return;
    }

    lockCount -= 1;

    if (lockCount > 0) {
        return;
    }

    const restore = restoreScrollLock;
    restoreScrollLock = undefined;
    restore?.();
};

export const useBodyScrollBlock = (enabled = true) =>
    useIsomorphicLayoutEffect(() => {
        if (!enabled || !lockBodyScroll()) {
            return;
        }

        return unlockBodyScroll;
    }, [enabled]);
